/**
 * @element qds-column-header
 *
 * A single sortable table header cell. Built as its own component so sorting,
 * alignment and resize behaviour are specified and tested once, rather than
 * reimplemented inside qds-data-table and again in every bespoke grid in Q360.
 *
 * ## The `<th>` composition rule
 *
 * A `<th>` must be a direct child of `<tr>` for the table layout algorithm to
 * work. A custom element cannot be a `<th>`. Resolution: `qds-data-table`
 * creates real `<th>` elements and puts a `qds-column-header` inside each one.
 * The header component sets `display: block; width: 100%` on its host so it
 * fills the cell.
 *
 * The `<th>` owns `aria-sort`. This component NEVER sets `aria-sort` on its own
 * host. `qds-data-table` writes `aria-sort="ascending"`, `"descending"` or
 * `"none"` onto the `<th>`.
 *
 * @attr {string} key - Column key; echoed in sort and resize events
 * @attr {string} label - Column label text; also settable as slotted text
 * @attr {('start'|'center'|'end')} align - Text alignment; default 'start'
 * @attr {boolean} sortable - Whether the column is sortable
 * @attr {('asc'|'desc'|'')} sort-direction - Current sort direction; empty = unsorted
 * @attr {boolean} resizable - Whether the column is resizable; renders drag handle
 * @attr {('start'|'end'|'')} sticky - Sticky positioning pass-through; the table owns offsets
 * @attr {string} aria-label-text - Accessible name when label is an icon or abbreviation
 * @attr {string} sort-priority - Multi-sort priority number to display next to the indicator
 *
 * @csspart header - The header container
 * @csspart label - The label text element
 * @csspart sort-indicator - The sort direction chevron
 * @csspart sort-priority - The multi-sort priority number
 * @csspart resize-handle - The column resize drag handle
 *
 * @fires qds-sort - When header activated by click, Enter or Space. detail: {component, key, direction, additive}
 * @fires qds-resize-start - When drag handle pressed. detail: {component, key, width}
 * @fires qds-resize - During drag, throttled to requestAnimationFrame. detail: {component, key, width}
 * @fires qds-resize-end - When drag released. detail: {component, key, width}
 *
 * @example
 * <th aria-sort="descending">
 *   <qds-column-header
 *     key="marketValue"
 *     label="Market value"
 *     sortable
 *     sort-direction="desc"
 *     align="end"
 *   ></qds-column-header>
 * </th>
 */

import { loadBaseCSS } from '../shared/button-utils.js';

interface QdsColumnHeaderSortEventDetail {
  component: HTMLElement;
  key: string;
  direction: 'asc' | 'desc' | null;
  additive: boolean;
}

interface QdsColumnHeaderResizeEventDetail {
  component: HTMLElement;
  key: string;
  width: number;
}

export class QdsColumnHeader extends HTMLElement {
  private _key: string = '';
  private _label: string = '';
  private _align: 'start' | 'center' | 'end' = 'start';
  private _sortable: boolean = false;
  private _sortDirection: 'asc' | 'desc' | '' = '';
  private _resizable: boolean = false;
  private _sticky: 'start' | 'end' | '' = '';
  private _ariaLabelText: string = '';
  private _sortPriority: string = '';

  // Resize drag state
  private _resizeActive: boolean = false;
  private _resizeStartX: number = 0;
  private _resizeStartWidth: number = 0;
  private _resizeCurrentWidth: number = 0;
  private _resizeRafId: number | null = null;
  private _resizeWidthDirty: number | null = null;

  // Bound handlers for resize drag (added to window during drag)
  private _boundPointerMove: ((e: PointerEvent) => void) | null = null;
  private _boundPointerUp: ((e: PointerEvent) => void) | null = null;

  // Cleanup for listeners added during render
  private _cleanup?: () => void;

  static get observedAttributes(): string[] {
    return [
      'key',
      'label',
      'align',
      'sortable',
      'sort-direction',
      'resizable',
      'sticky',
      'aria-label-text',
      'sort-priority',
    ];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    loadBaseCSS();
  }

  connectedCallback(): void {
    if (!this.isConnected) return;
    this._readAttributes();
    this.render();
  }

  disconnectedCallback(): void {
    this._cleanupListeners();
    this._endResizeDrag(false);
  }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ): void {
    if (oldValue === newValue) return;
    this._readAttribute(name, newValue);
    if (this.isConnected) {
      this.render();
    }
  }

  // ── Attribute reading ─────────────────────────────────────────────

  private _readAttributes(): void {
    this._key = this.getAttribute('key') || '';
    this._label = this.getAttribute('label') || '';
    const align = this.getAttribute('align');
    this._align = align === 'center' || align === 'end' ? align : 'start';
    this._sortable = this.hasAttribute('sortable');
    const dir = this.getAttribute('sort-direction');
    this._sortDirection = dir === 'asc' || dir === 'desc' ? dir : '';
    this._resizable = this.hasAttribute('resizable');
    const sticky = this.getAttribute('sticky');
    this._sticky = sticky === 'start' || sticky === 'end' ? sticky : '';
    this._ariaLabelText = this.getAttribute('aria-label-text') || '';
    this._sortPriority = this.getAttribute('sort-priority') || '';
  }

  private _readAttribute(name: string, value: string | null): void {
    switch (name) {
      case 'key':
        this._key = value || '';
        break;
      case 'label':
        this._label = value || '';
        break;
      case 'align':
        this._align = value === 'center' || value === 'end' ? value : 'start';
        break;
      case 'sortable':
        this._sortable = value !== null;
        break;
      case 'sort-direction':
        this._sortDirection = value === 'asc' || value === 'desc' ? value : '';
        break;
      case 'resizable':
        this._resizable = value !== null;
        break;
      case 'sticky':
        this._sticky = value === 'start' || value === 'end' ? value : '';
        break;
      case 'aria-label-text':
        this._ariaLabelText = value || '';
        break;
      case 'sort-priority':
        this._sortPriority = value || '';
        break;
    }
  }

  // ── Sort logic ─────────────────────────────────────────────────────

  /**
   * Advance the sort cycle: unsorted -> asc -> desc -> unsorted.
   * Returns the direction being requested (not the previous one).
   */
  private _advanceSort(): 'asc' | 'desc' | null {
    if (this._sortDirection === 'asc') return 'desc';
    if (this._sortDirection === 'desc') return null;
    return 'asc';
  }

  /**
   * Handle sort activation from click or keyboard.
   * `shiftKey` is read from MouseEvent or KeyboardEvent.
   */
  private _handleSortActivate(e: Event): void {
    if (!this._sortable) return;
    const shiftKey =
      (e as MouseEvent).shiftKey || (e as KeyboardEvent).shiftKey;
    const direction = this._advanceSort();

    // Update the attribute so the indicator reflects the new state.
    // attributeChangedCallback will re-render.
    if (direction === null) {
      this.removeAttribute('sort-direction');
    } else {
      this.setAttribute('sort-direction', direction);
    }

    this.dispatchEvent(
      new CustomEvent<QdsColumnHeaderSortEventDetail>('qds-sort', {
        bubbles: true,
        composed: true,
        detail: {
          component: this,
          key: this._key,
          direction,
          additive: !!shiftKey,
        },
      }),
    );
  }

  // ── Keyboard ───────────────────────────────────────────────────────

  private _handleHostKeyDown(e: KeyboardEvent): void {
    if (!this._sortable) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this._handleSortActivate(e);
    }
  }

  private _handleResizeKeyDown(e: KeyboardEvent): void {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this._adjustWidth(-8);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this._adjustWidth(8);
    }
  }

  private _adjustWidth(delta: number): void {
    const currentWidth = this.offsetWidth;
    const newWidth = Math.max(0, currentWidth + delta);
    this._resizeCurrentWidth = newWidth;
    this._dispatchResizeEvent('qds-resize-start', currentWidth);
    this._dispatchResizeEvent('qds-resize', newWidth);
    this._dispatchResizeEvent('qds-resize-end', newWidth);
  }

  // ── Pointer drag for resize ────────────────────────────────────────

  private _handleResizePointerDown(e: PointerEvent): void {
    e.preventDefault();
    this._resizeStartX = e.clientX;
    this._resizeStartWidth = this.offsetWidth;
    this._resizeCurrentWidth = this._resizeStartWidth;
    this._resizeActive = true;

    this._dispatchResizeEvent(
      'qds-resize-start',
      this._resizeCurrentWidth,
    );

    this._boundPointerMove = (ev: PointerEvent) =>
      this._handleResizePointerMove(ev);
    this._boundPointerUp = (ev: PointerEvent) =>
      this._handleResizePointerUp(ev);

    window.addEventListener('pointermove', this._boundPointerMove);
    window.addEventListener('pointerup', this._boundPointerUp);
  }

  private _handleResizePointerMove(e: PointerEvent): void {
    if (!this._resizeActive) return;
    const newWidth =
      this._resizeStartWidth + (e.clientX - this._resizeStartX);
    this._resizeWidthDirty = newWidth;

    if (this._resizeRafId === null) {
      this._resizeRafId = requestAnimationFrame(() => {
        this._resizeRafId = null;
        if (this._resizeWidthDirty !== null) {
          this._resizeCurrentWidth = this._resizeWidthDirty;
          this._resizeWidthDirty = null;
          this._dispatchResizeEvent(
            'qds-resize',
            this._resizeCurrentWidth,
          );
        }
      });
    }
  }

  private _handleResizePointerUp(_e: PointerEvent): void {
    this._endResizeDrag(true);
  }

  /**
   * Tear down the resize drag: cancel rAF, remove window listeners.
   * @param fireEnd When true, dispatch qds-resize-end if a drag was active.
   */
  private _endResizeDrag(fireEnd: boolean): void {
    if (this._resizeRafId !== null) {
      cancelAnimationFrame(this._resizeRafId);
      this._resizeRafId = null;
    }
    if (this._boundPointerMove) {
      window.removeEventListener('pointermove', this._boundPointerMove);
      this._boundPointerMove = null;
    }
    if (this._boundPointerUp) {
      window.removeEventListener('pointerup', this._boundPointerUp);
      this._boundPointerUp = null;
    }
    if (fireEnd && this._resizeActive) {
      this._dispatchResizeEvent(
        'qds-resize-end',
        this._resizeCurrentWidth,
      );
    }
    this._resizeActive = false;
    this._resizeWidthDirty = null;
  }

  // ── Event dispatch ─────────────────────────────────────────────────

  private _dispatchResizeEvent(name: string, width: number): void {
    this.dispatchEvent(
      new CustomEvent<QdsColumnHeaderResizeEventDetail>(name, {
        bubbles: true,
        composed: true,
        detail: {
          component: this,
          key: this._key,
          width,
        },
      }),
    );
  }

  // ── Listener setup / cleanup ───────────────────────────────────────

  private _cleanupListeners(): void {
    if (this._cleanup) {
      this._cleanup();
      this._cleanup = undefined;
    }
  }

  private _setupListeners(): void {
    const cleanups: Array<() => void> = [];

    // Sort button click (mouse path)
    const sortBtn = this.shadowRoot?.querySelector<HTMLElement>('#sort-btn');
    if (sortBtn) {
      const onClick = (e: MouseEvent) => this._handleSortActivate(e);
      sortBtn.addEventListener('click', onClick);
      cleanups.push(() => sortBtn.removeEventListener('click', onClick));
    }

    // Host keydown for sort (keyboard path)
    if (this._sortable) {
      const onKeyDown = (e: KeyboardEvent) => this._handleHostKeyDown(e);
      this.addEventListener('keydown', onKeyDown);
      cleanups.push(() => this.removeEventListener('keydown', onKeyDown));
    }

    // Resize handle
    const handle = this.shadowRoot?.querySelector<HTMLElement>(
      '#resize-handle',
    );
    if (handle) {
      const onPointerDown = (e: PointerEvent) =>
        this._handleResizePointerDown(e);
      const onKeyDown = (e: KeyboardEvent) =>
        this._handleResizeKeyDown(e);
      handle.addEventListener('pointerdown', onPointerDown);
      handle.addEventListener('keydown', onKeyDown);
      cleanups.push(() => {
        handle.removeEventListener('pointerdown', onPointerDown);
        handle.removeEventListener('keydown', onKeyDown);
      });
    }

    this._cleanup = () => cleanups.forEach((fn) => fn());
  }

  // ── Tabindex management ────────────────────────────────────────────

  private _updateTabindex(): void {
    if (this._sortable) {
      this.setAttribute('tabindex', '0');
    } else {
      this.removeAttribute('tabindex');
    }
  }

  // ── ARIA ────────────────────────────────────────────────────────────

  private _sortAriaLabel(): string {
    const name = this._ariaLabelText || this._label || 'column';
    let state = '';
    if (this._sortDirection === 'asc') state = ', sorted ascending';
    else if (this._sortDirection === 'desc') state = ', sorted descending';
    return `${name}${state}`;
  }

  private _resizeAriaLabel(): string {
    const name = this._label || this._ariaLabelText || 'column';
    return `Resize ${name} column`;
  }

  // ── HTML escaping ──────────────────────────────────────────────────

  private _escape(s: string): string {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ── Rendering ──────────────────────────────────────────────────────

  private _styles(): string {
    return `
      :host {
        display: block;
        width: 100%;
      }

      .header {
        display: flex;
        align-items: center;
        padding: 0 var(--qds-column-header-padding-x, var(--theme-space-3, 12px));
        background: var(--qds-column-header-background, var(--theme-background-tertiary, #e9ecef));
        color: var(--qds-column-header-color, var(--theme-text-secondary, #555));
        border-bottom: 1px solid var(--qds-column-header-border-color, var(--theme-border-strong, #AFBBD1));
        font-size: var(--qds-column-header-font-size, 13px);
        font-weight: 600;
        min-height: var(--qds-control-height-md, 40px);
        box-sizing: border-box;
        text-transform: none;
      }

      .sort-btn {
        flex: 1;
        display: flex;
        align-items: center;
        gap: var(--theme-space-1, 4px);
        background: none;
        border: none;
        padding: 0;
        margin: 0;
        color: inherit;
        font: inherit;
        cursor: pointer;
        text-align: inherit;
        min-height: inherit;
      }

      .sort-btn:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px var(--button-focus-ring, rgba(68, 94, 137, 0.25));
      }

      .label-wrapper {
        flex: 1;
        display: flex;
        align-items: center;
        gap: var(--theme-space-1, 4px);
        min-height: inherit;
      }

      .label {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      /* Alignment */
      .header.align-start .sort-btn,
      .header.align-start .label-wrapper {
        justify-content: flex-start;
      }
      .header.align-center .sort-btn,
      .header.align-center .label-wrapper {
        justify-content: center;
      }
      .header.align-end .sort-btn,
      .header.align-end .label-wrapper {
        justify-content: flex-end;
      }

      /* Sort indicator — 12px chevron */
      .sort-indicator {
        flex-shrink: 0;
        width: 12px;
        height: 12px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity 0.15s;
      }

      .sort-indicator::before {
        content: '';
        display: block;
        width: 0;
        height: 0;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
      }

      /* Active states — accent primary */
      .sort-indicator.asc {
        opacity: 1;
      }
      .sort-indicator.asc::before {
        border-bottom: 6px solid var(--theme-accent-primary, #445E89);
      }

      .sort-indicator.desc {
        opacity: 1;
      }
      .sort-indicator.desc::before {
        border-top: 6px solid var(--theme-accent-primary, #445E89);
      }

      /* Inactive sortable — subtle on hover */
      .sort-btn:hover .sort-indicator:not(.asc):not(.desc) {
        opacity: 0.4;
      }
      .sort-indicator:not(.asc):not(.desc)::before {
        border-bottom: 6px solid var(--theme-text-tertiary, #6A6A6A);
      }

      /* Sort priority number */
      .sort-priority {
        flex-shrink: 0;
        font-size: 10px;
        font-weight: 600;
        color: var(--theme-text-tertiary, #6A6A6A);
        line-height: 1;
      }

      /* Resize handle */
      .resize-handle {
        flex-shrink: 0;
        width: 4px;
        align-self: stretch;
        margin-left: var(--theme-space-1, 4px);
        background: var(--theme-border-primary, #E4E9F2);
        border: none;
        border-radius: var(--theme-radius-sm, 4px);
        cursor: col-resize;
        padding: 0;
      }

      .resize-handle:hover {
        background: var(--theme-accent-primary, #445E89);
      }

      .resize-handle:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px var(--button-focus-ring, rgba(68, 94, 137, 0.25));
      }

      /* Coarse pointer — enlarge touch targets */
      @media (pointer: coarse) {
        .header {
          min-height: 44px;
        }
        .resize-handle {
          width: 8px;
        }
      }
    `;
  }

  private _template(): string {
    const escapedLabel = this._escape(this._label);
    const escapedPriority = this._escape(this._sortPriority);

    // Label: attribute takes priority, slot as fallback
    const labelEl = this._label
      ? `<span class="label" part="label" title="${escapedLabel}">${escapedLabel}</span>`
      : `<slot class="label" part="label"></slot>`;

    // Sort indicator (only when sortable)
    const indicatorEl = this._sortable
      ? `<span class="sort-indicator ${this._sortDirection}" part="sort-indicator"></span>`
      : '';

    // Priority number (when sortable and priority is set)
    const priorityEl =
      this._sortable && this._sortPriority
        ? `<span class="sort-priority" part="sort-priority">${escapedPriority}</span>`
        : '';

    // DOM order: align="end" puts indicator before label so label stays
    // flush with right-aligned numerals below.
    let content: string;
    if (this._align === 'end') {
      content = `${indicatorEl}${priorityEl}${labelEl}`;
    } else {
      content = `${labelEl}${priorityEl}${indicatorEl}`;
    }

    // Interactive wrapper: real <button> when sortable for ARIA
    const interactive = this._sortable
      ? `<button class="sort-btn" id="sort-btn" type="button" tabindex="-1" aria-label="${this._escape(this._sortAriaLabel())}">${content}</button>`
      : `<span class="label-wrapper">${content}</span>`;

    // Resize handle (separate tab stop)
    const resizeHandle = this._resizable
      ? `<button class="resize-handle" id="resize-handle" part="resize-handle" type="button" tabindex="0" aria-label="${this._escape(this._resizeAriaLabel())}"></button>`
      : '';

    return `
      <style>${this._styles()}</style>
      <div class="header align-${this._align}" part="header">
        ${interactive}
        ${resizeHandle}
      </div>
    `;
  }

  private render(): void {
    this._cleanupListeners();
    if (!this.shadowRoot) return;
    this.shadowRoot.innerHTML = this._template();
    this._updateTabindex();
    this._setupListeners();
  }
}

if (!customElements.get('qds-column-header')) {
  customElements.define('qds-column-header', QdsColumnHeader);
}
